import './login.page.css'
import logo from '../../../assets/images/logo.png'
import { Link } from 'react-router-dom'
import { useAuth, useLoginForm } from '../auth.hooks'
import { LoadingButton } from '@mui/lab'
import { FormProvider } from 'react-hook-form'
import TextInputField from '../../../components/fields/text-input-field/text-input-field.component'
import {
  Alert,
  Snackbar,
  Stack,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material'
import { useState, useEffect } from 'react'
import useSubmitLock from 'hooks/use-submit-lock'

const LoginPage = () => {
  const formMethods = useLoginForm()
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false)
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false)
  const [forgotPasswordError, setForgotPasswordError] = useState('')
  const [isSendingEmail, setIsSendingEmail] = useState(false)

  const { isSubmitting, wrapFormSubmit } = useSubmitLock()

  const {
    handleLogin,
    handleForgotPassword,
    isLoginLoading,
    loginError,
    loginErrorOpen,
    setLoginErrorOpen,
  } = useAuth()

  const handleForgotPasswordClick = () => {
    setForgotPasswordError('')
    setForgotPasswordSuccess(false)
    setForgotPasswordOpen(true)
  }

  useEffect(() => {
    if (forgotPasswordOpen) {
      const email = formMethods.watch('username')
      if (!email || email.trim() === '') {
        setForgotPasswordError('Please enter your email address first')
        return
      }

      // Auto-send email when dialog opens
      const sendEmail = async () => {
        setIsSendingEmail(true)
        setForgotPasswordError('')
        const result = await handleForgotPassword(email)
        setIsSendingEmail(false)

        if (result.success) {
          setForgotPasswordSuccess(true)
        } else {
          setForgotPasswordError('Incorrect Mail address')
        }
      }

      sendEmail()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forgotPasswordOpen])

  const handleCloseForgotPassword = () => {
    setForgotPasswordOpen(false)
    setForgotPasswordError('')
    setForgotPasswordSuccess(false)
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <h1>Welcome</h1>
        <img src={logo} alt="Transpots Logo" className="logo" />
        <FormProvider {...formMethods}>
          <form
            onSubmit={wrapFormSubmit(formMethods.handleSubmit, (data) =>
              handleLogin(data)
            )}
          >
            <Stack spacing={2} sx={{ width: '320px' }}>
              <TextInputField
                name="username"
                placeholder="Email"
                type="email"
                variant="standard"
              />
              <TextInputField
                name="password"
                placeholder="Password"
                type="password"
                showPasswordToggle
                variant="standard"
              />
              <LoadingButton
                variant="contained"
                fullWidth
                loading={isLoginLoading || isSubmitting}
                disabled={isLoginLoading || isSubmitting}
                sx={{
                  borderRadius: '4px',
                  '&.Mui-disabled': {
                    bgcolor: 'action.disabledBackground',
                    color: 'action.disabled',
                  },
                }}
                type="submit"
              >
                LOGIN
              </LoadingButton>
            </Stack>
            <div className="terms-text">
              By logging in, you agree to our{' '}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="terms-link"
              >
                Terms and Privacy Policy
              </a>
              .
            </div>
          </form>
        </FormProvider>

        <div className="form-footer">
          <Link to="/auth/signup">Create Account</Link>
          <span
            onClick={handleForgotPasswordClick}
            style={{ color: '#0075ff', cursor: 'pointer', textDecoration: 'none' }}
          >
            Forgot Password?
          </span>
        </div>
      </div>

      <Snackbar
        open={loginErrorOpen}
        autoHideDuration={6000}
        onClose={() => setLoginErrorOpen(false)}
      >
        <Alert severity="error">{loginError}</Alert>
      </Snackbar>

      <Dialog
        open={forgotPasswordOpen}
        onClose={handleCloseForgotPassword}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center' }}>
          {forgotPasswordSuccess ? (
            <Typography>
              Mail sent successfully. Please reset your password using the link
              sent to your email address.
            </Typography>
          ) : (
            <>
              {forgotPasswordError && (
                <Typography>{forgotPasswordError}</Typography>
              )}
              {isSendingEmail && (
                <Typography>Sending reset email...</Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {forgotPasswordSuccess ? (
            <Button onClick={handleCloseForgotPassword} variant="contained">
              Close
            </Button>
          ) : (
            <Button onClick={handleCloseForgotPassword}>Cancel</Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default LoginPage
